"use client"

import { useState, useEffect, Suspense } from "react"
import { useSearchParams, useRouter } from "next/navigation"
import Link from "next/link"
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { authService } from "@/services/auth"
import { TurnstileWidget } from "@/components/landing/turnstile-widget"
import { useTurnstile } from "@/hooks/use-turnstile"
import { Loader2, LogIn, Target, Eye, EyeOff, ShieldCheck, ExternalLink, Copy, Check } from "lucide-react"
import { toast } from "sonner"
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSlot,
} from "@/components/ui/input-otp"

export default function LoginPage() {
    return (
        <div className="flex items-center justify-center min-h-screen bg-muted/40 p-4 relative overflow-hidden">
            {/* Ambient Background */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-[20%] -left-[10%] w-[70vw] h-[70vw] rounded-full bg-primary/5 blur-[100px] animate-blob" />
                <div className="absolute -bottom-[20%] -right-[10%] w-[70vw] h-[70vw] rounded-full bg-blue-500/5 blur-[100px] animate-blob animation-delay-2000" />
            </div>

            <div className="w-full max-w-[380px] bg-background/60 backdrop-blur-xl border-white/20 p-6 md:p-8 rounded-[1.5rem] shadow-2xl border relative z-10 transition-all duration-300 hover:shadow-primary/5">
                <Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}>
                    <LoginForm />
                </Suspense>
            </div>
        </div>
    )
}

import { useAuthUI } from "@/contexts/AuthUIContext"

function isFacebookInAppBrowser(): boolean {
    if (typeof navigator === "undefined") return false
    const ua = navigator.userAgent || ""
    return /FBAN|FBAV|FB_IAB|FB4A|FBIOS|Instagram|Messenger|\[FB_IAB\]/.test(ua)
}

function LoginForm() {
    const router = useRouter()
    const searchParams = useSearchParams()
    const token = searchParams?.get('token')
    const next = searchParams?.get('next')
    const redirectUrl = next ? decodeURIComponent(next) : '/dashboard'
    const { setHasLoginError, incrementFailedAttempts, resetFailedAttempts } = useAuthUI()

    const [isLoading, setIsLoading] = useState(false)
    const captcha = useTurnstile()
    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const [showPassword, setShowPassword] = useState(false)
    const [requires2FA, setRequires2FA] = useState(false)
    const [otp, setOtp] = useState("")
    const [emailError, setEmailError] = useState("")
    const [passwordError, setPasswordError] = useState("")
    const [isFBBrowser, setIsFBBrowser] = useState(false)
    const [isAndroid, setIsAndroid] = useState(false)
    const [copied, setCopied] = useState(false)

    useEffect(() => {
        setIsFBBrowser(isFacebookInAppBrowser())
        setIsAndroid(/Android/i.test(navigator.userAgent || ""))
    }, [])

    const handleOpenInBrowser = () => {
        const url = window.location.href
        if (isAndroid) {
            // Android: intent URL forces Chrome to open instead of staying in IAB
            const host = url.replace(/^https?:\/\//, '')
            window.location.href = `intent://${host}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(url)};end`
        } else {
            // iOS: programmatic browser switching is blocked; copy link + show instructions
            navigator.clipboard?.writeText(url).then(() => {
                setCopied(true)
                setTimeout(() => setCopied(false), 2500)
            }).catch(() => {
                toast.info("Copy the link from your address bar and paste it into Safari.")
            })
        }
    }

    useEffect(() => {
        if (token) {
            authService.setToken(token)
            toast.success("Successfully logged in via SSO")
            window.location.href = redirectUrl
        }
    }, [token, redirectUrl])

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!requires2FA && (!email || !password)) {
            toast.error("Please fill in all fields")
            return
        }

        setEmailError("")
        setPasswordError("")
        setIsLoading(true)
        setHasLoginError(false)
        try {
            if (requires2FA) {
                if (otp.length !== 6) {
                    toast.error("Please enter the 6-digit verification code")
                    setIsLoading(false)
                    return
                }
                const cleanEmail = email.trim().replace(/^"|"$/g, '')
                await authService.verify2FALogin(cleanEmail, otp)
                toast.success("Welcome back!")
                resetFailedAttempts()
                window.location.href = redirectUrl
            } else {
                const cleanEmail = email.trim().replace(/^"|"$/g, '')
                const cleanPassword = password.replace(/^"|"$/g, '')

                const response = await authService.login({
                    email: cleanEmail,
                    password: cleanPassword,
                    turnstile_token: captcha.token,
                })

                if (response?.requires_2fa) {
                    setRequires2FA(true)
                    toast.info(response.message || "A verification code has been sent to your email.")
                } else {
                    toast.success("Welcome back!")
                    resetFailedAttempts() // Reset on success
                    window.location.href = redirectUrl
                }
            }
        } catch (error: any) {
            console.error("Login failed:", error)
            const status = error.response?.status
            const detail = error.response?.data?.detail

            // 428 = the bot check, not the credentials. Showing it under the
            // password field or counting it toward a lockout would be wrong on
            // both counts.
            if (status === 428) {
                captcha.reset()
                toast.error("Verification needed", {
                    description:
                        detail ||
                        "Please complete the verification check. If it will not load, disable your ad blocker and refresh.",
                })
                return
            }

            setHasLoginError(true)
            incrementFailedAttempts()
            if (!requires2FA && status === 404) {
                setEmailError(detail || "No account found with this email address. Please sign up first.")
            } else if (!requires2FA && status === 400) {
                setPasswordError(detail || "Incorrect password. Please try again.")
            } else {
                toast.error(requires2FA ? "Verification failed" : "Login failed", {
                    description: detail || "Invalid email, password, or verification code"
                })
            }
        } finally {
            setIsLoading(false)
        }
    }



    return (
        <div className="grid gap-5">
            <div className="grid gap-2 text-center">
                <h1 className="text-3xl font-bold tracking-tight">
                    {requires2FA ? "2-Step Verification" : "Welcome back"}
                </h1>
                <p className="text-muted-foreground text-sm">
                    {requires2FA
                        ? "Enter the 6-digit verification code sent to your email"
                        : "Enter your email to sign in to your account"}
                </p>
            </div>

            <form onSubmit={handleLogin} className="grid gap-4">
                {!requires2FA ? (
                    <>
                        <div className="grid gap-2">
                            <Label htmlFor="email" className="text-sm font-medium text-muted-foreground">Email Address</Label>
                            <div className="relative group">
                                <Target className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="m@example.com"
                                    className={`pl-9 h-10 bg-muted/50 border-none rounded-lg focus-visible:ring-1 focus-visible:ring-primary/50 ${emailError ? 'ring-1 ring-destructive' : ''}`}
                                    value={email}
                                    onChange={(e) => { setEmail(e.target.value); setEmailError("") }}
                                    disabled={isLoading}
                                    required
                                />
                            </div>
                            {emailError && (
                                <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                                    <span className="inline-block w-3.5 h-3.5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                                    {emailError}
                                </p>
                            )}
                        </div>

                        <div className="grid gap-2">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="password" className="text-sm font-medium text-muted-foreground">Password</Label>
                                <Link
                                    href="/forgot-password"
                                    className="text-sm font-medium text-primary hover:underline"
                                >
                                    Forgot?
                                </Link>
                            </div>
                            <div className="relative group">
                                <LogIn className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="••••••••"
                                    className={`pl-9 pr-9 h-10 bg-muted/50 border-none rounded-lg focus-visible:ring-1 focus-visible:ring-primary/50 transition-all duration-200 ${passwordError ? 'ring-1 ring-destructive' : ''}`}
                                    value={password}
                                    onChange={(e) => { setPassword(e.target.value); setPasswordError("") }}
                                    disabled={isLoading}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground transition-colors focus:outline-none"
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                            {passwordError && (
                                <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                                    <span className="inline-block w-3.5 h-3.5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                                    {passwordError}
                                </p>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="grid gap-4">
                        <div className="flex flex-col items-center justify-center space-y-4 py-4">
                            <div className="p-3 bg-primary/10 rounded-full mb-2">
                                <ShieldCheck className="h-8 w-8 text-primary" />
                            </div>
                            <InputOTP
                                maxLength={6}
                                value={otp}
                                onChange={setOtp}
                                disabled={isLoading}
                                className="gap-2"
                            >
                                <InputOTPGroup>
                                    <InputOTPSlot index={0} className="w-10 h-12 text-lg sm:w-12 sm:h-14 sm:text-xl border-border bg-background/50" />
                                    <InputOTPSlot index={1} className="w-10 h-12 text-lg sm:w-12 sm:h-14 sm:text-xl border-border bg-background/50" />
                                    <InputOTPSlot index={2} className="w-10 h-12 text-lg sm:w-12 sm:h-14 sm:text-xl border-border bg-background/50" />
                                    <InputOTPSlot index={3} className="w-10 h-12 text-lg sm:w-12 sm:h-14 sm:text-xl border-border bg-background/50" />
                                    <InputOTPSlot index={4} className="w-10 h-12 text-lg sm:w-12 sm:h-14 sm:text-xl border-border bg-background/50" />
                                    <InputOTPSlot index={5} className="w-10 h-12 text-lg sm:w-12 sm:h-14 sm:text-xl border-border bg-background/50" />
                                </InputOTPGroup>
                            </InputOTP>
                        </div>
                    </div>
                )}

                {/* Credential step only — the 2FA step reuses the same form and
                    the token has already been spent by then. */}
                {!requires2FA && captcha.siteKey && (
                    <div className="mt-2">
                        <TurnstileWidget
                            siteKey={captcha.siteKey}
                            theme="auto"
                            onVerify={captcha.onVerify}
                            onExpire={captcha.onExpire}
                            onError={captcha.onError}
                        />
                        {captcha.failed && (
                            <p className="mt-2 text-center text-xs text-muted-foreground">
                                The verification check could not load. You can still try to sign in.
                            </p>
                        )}
                    </div>
                )}

                <Button
                    type="submit"
                    className="w-full h-10 mt-2 rounded-lg bg-primary hover:bg-primary/90 shadow-md font-bold text-sm"
                    disabled={isLoading || (!requires2FA && captcha.blocking)}
                >
                    {isLoading ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {requires2FA ? "Verifying..." : "Signing in..."}
                        </>
                    ) : (
                        requires2FA ? "Verify & Sign In" : "Sign In"
                    )}
                </Button>

                {requires2FA && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                            setRequires2FA(false);
                            setOtp("");
                        }}
                        className="text-muted-foreground hover:text-foreground mt-[-8px] text-sm h-8"
                    >
                        Cancel
                    </Button>
                )}
            </form>

            {!requires2FA && (
                isFBBrowser ? (
                    <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 grid gap-3">
                        <p className="text-sm text-amber-700 dark:text-amber-400 font-medium leading-snug text-center">
                            Google login is disabled in this browser for security.
                        </p>

                        {isAndroid ? (
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full h-10 rounded-lg border-amber-500/50 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10 text-sm font-medium"
                                onClick={handleOpenInBrowser}
                            >
                                <ExternalLink className="mr-2 h-4 w-4" />
                                Open in Chrome
                            </Button>
                        ) : (
                            <>
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full h-10 rounded-lg border-amber-500/50 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10 text-sm font-medium"
                                    onClick={handleOpenInBrowser}
                                >
                                    {copied ? (
                                        <><Check className="mr-2 h-4 w-4" />Link Copied!</>
                                    ) : (
                                        <><Copy className="mr-2 h-4 w-4" />Copy Link
                                        </>
                                    )}
                                </Button>
                                <p className="text-xs text-amber-600 dark:text-amber-500 text-center leading-relaxed">
                                    Tap <span className="font-bold">···</span> (top right) → <span className="font-bold">Open in external browser</span>, or paste the copied link into Safari.
                                </p>
                            </>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <Separator className="w-full" />
                            </div>
                            <div className="relative flex justify-center text-sm font-medium">
                                <span className="bg-card px-3 text-muted-foreground">
                                    Or continue with
                                </span>
                            </div>
                        </div>

                        <div className="grid grid-cols-1">
                            <Button
                                variant="outline"
                                className="w-full h-10 rounded-lg border-border hover:bg-muted/50 text-sm font-medium"
                                onClick={() => {
                                    const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID
                                    const redirectUri = process.env.NEXT_PUBLIC_GOOGLE_REDIRECT_URI || `${window.location.origin}/auth/google/callback`
                                    const state = encodeURIComponent(redirectUrl)
                                    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=openid%20email%20profile&state=${state}`
                                    window.location.href = authUrl
                                }}
                                disabled={isLoading}
                            >
                                <svg className="mr-2 h-3.5 w-3.5" viewBox="0 0 24 24">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                    <path d="M1 1h22v22H1z" fill="none" />
                                </svg>
                                Google
                            </Button>
                        </div>
                    </>
                )
            )}

            <p className={`text-center text-sm text-muted-foreground mt-1 transition-opacity duration-300 ${requires2FA ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                Don&apos;t have an account?{" "}
                <Link href={next ? `/register?next=${encodeURIComponent(next)}` : "/register"} className="font-bold text-primary hover:underline">
                    Sign up
                </Link>
            </p>
        </div>
    )
}

